export const ROUTING_SCOPES = ['global', 'user', 'property'];
export const DEFAULT_CHANNELS = ['email'];
export const TICKET_PRIORITIES = ['low', 'medium', 'high', 'urgent'];

export const slugify = (raw) => {
  if (raw == null || typeof raw !== 'string') return '';
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

/** Normalize a free-form issue type tag for routing rules (underscore-separated). */
export const normalizeIssueType = (raw) => {
  if (raw == null || typeof raw !== 'string') return '';
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

export const normalizeIssueTypes = (value) => {
  if (value == null) return [];
  const arr = Array.isArray(value) ? value : [value];
  const seen = new Set();
  const out = [];
  for (const item of arr) {
    const normalized = normalizeIssueType(String(item));
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
};

export const collectIssueTypeSuggestions = ({
  partners = [],
  rules = [],
  selectedPartnerId,
} = {}) => {
  const set = new Set();

  const addLines = (lines = []) => {
    for (const line of lines) {
      const normalized = normalizeIssueType(line);
      if (normalized) set.add(normalized);
    }
  };

  for (const partner of partners) {
    addLines(partner.serviceLines);
  }

  if (selectedPartnerId) {
    const partner = partners.find((p) => String(p.id) === String(selectedPartnerId));
    addLines(partner?.serviceLines);
  }

  for (const rule of rules) {
    for (const issueType of rule.issueTypes || []) {
      const normalized = normalizeIssueType(issueType);
      if (normalized) set.add(normalized);
    }
  }

  return [...set].sort();
};

export const isAdminAppUser = (user) => {
  if (!user) return false;
  const meta = user.publicMetadata || {};
  const role = meta.role;
  const appAccess = meta.access && typeof meta.access === 'object' ? meta.access.app : undefined;
  return role === 'admin' && appAccess === true;
};

export const parseEmailsInput = (input) => {
  if (input == null) return [];
  if (Array.isArray(input)) {
    return input.map((e) => String(e).trim()).filter(Boolean);
  }
  return String(input)
    .split(/[,\n]/)
    .map((e) => e.trim())
    .filter(Boolean);
};

export const parseStringList = (input) => {
  if (input == null) return [];
  if (Array.isArray(input)) {
    return input.map((s) => String(s).trim()).filter(Boolean);
  }
  return String(input)
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

export const formatEmails = (emails) => {
  if (!Array.isArray(emails)) return '';
  return emails.join(', ');
};

export const formatStringList = (items) => {
  if (!Array.isArray(items)) return '';
  return items.join(', ');
};

export const parseConditionsInput = (input) => {
  if (input == null || input === '') return {};
  if (typeof input === 'object') return input;
  try {
    const parsed = JSON.parse(input);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    return {};
  } catch {
    throw new Error('Conditions must be valid JSON object');
  }
};

export const formatConditions = (conditions) => {
  if (!conditions || typeof conditions !== 'object') return '{}';
  try {
    return JSON.stringify(conditions, null, 2);
  } catch {
    return '{}';
  }
};

export const sortRulesByPriority = (rules = []) => {
  return [...rules].sort((a, b) => {
    const ap = a?.priorityOrder ?? 0;
    const bp = b?.priorityOrder ?? 0;
    if (ap === bp) return String(a?.id || '').localeCompare(String(b?.id || ''));
    return ap - bp;
  });
};

export const getPartnerOwnerScopeLabel = (partner) => {
  if (!partner) return '';
  return partner.ownerUserId ? `User: ${partner.ownerUserId}` : 'Global';
};
